import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  NativeSelect,
  Textarea,
  useToast,
} from '@provenance/design';
import {
  MasterDataListType,
  type DocumentDetail,
  type MasterDataItem,
} from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';

/** Shared invalidation after any decision, so every affected screen refreshes. */
function useDecisionMutation(documentId: string, onDone: () => void) {
  const queryClient = useQueryClient();
  return (onSuccess: () => void) => {
    void queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    void queryClient.invalidateQueries({ queryKey: ['documents'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    onSuccess();
    onDone();
  };
}

function useMasterData(listType: MasterDataListType) {
  return useQuery({
    queryKey: ['master-data', listType],
    queryFn: () => api.get<MasterDataItem[]>(`/api/console/master-data/${listType}`),
    staleTime: 5 * 60_000,
  });
}

/**
 * REJECT — with a structured reason (spec §7.1, §13.1).
 *
 * The reason is a dropdown from the chamber's managed list rather than free text. That is
 * the flagship master-data requirement, and the reason is straightforward: free-text
 * rejections are unreportable and inconsistent between operators, so a chamber can never
 * answer "what are we rejecting most, and why". "Other" with a free-text box covers the
 * genuine exception without reopening that hole.
 */
export function RejectDialog({
  document: doc,
  open,
  onOpenChange,
}: {
  document: DocumentDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const finish = useDecisionMutation(doc.id, () => onOpenChange(false));
  const reasons = useMasterData(MasterDataListType.REJECTION_REASON);

  const [reasonId, setReasonId] = useState('');
  const [freeText, setFreeText] = useState('');

  const isOther = reasonId === 'OTHER';
  const canSubmit = (reasonId && !isOther) || (isOther && freeText.trim().length > 0);

  const reject = useMutation({
    mutationFn: () =>
      api.post(`/api/console/documents/${doc.id}/reject`, {
        rejectionReasonId: isOther ? null : reasonId,
        freeText: freeText.trim() || null,
      }),
    onSuccess: () =>
      finish(() =>
        toast.toast({
          tone: 'success',
          title: 'Request rejected',
          description: `${doc.organisationName} has been emailed the reason, and the integrator has been notified.`,
        }),
      ),
    onError: (error) =>
      toast.error(
        'Could not reject this request',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  // Grouped by category so a list of twelve reasons stays scannable.
  const grouped = (reasons.data ?? []).reduce<Record<string, MasterDataItem[]>>((acc, item) => {
    const key = item.category ?? 'Other';
    (acc[key] ??= []).push(item);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow={doc.senderReference}
          title="Reject this request"
          description="No certificate is issued. The submitter is emailed the reason and can correct and resubmit."
        />
        <DialogBody className="flex flex-col gap-4">
          <Callout tone="info" icon={<Info className="size-4" />}>
            If the problem is fixable — a missing invoice, an unclear figure — <strong>Request
            clarification</strong> keeps the request alive and attached to this record instead.
          </Callout>

          <Field
            label="Reason"
            required
            help="From your chamber's managed list, so rejections stay reportable over time."
          >
            <NativeSelect value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
              <option value="">Choose a reason…</option>
              {Object.entries(grouped).map(([category, items]) => (
                <optgroup key={category} label={category}>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </optgroup>
              ))}
              <option value="OTHER">Other — describe it below</option>
            </NativeSelect>
          </Field>

          <Field
            label={isOther ? 'What is wrong' : 'Anything to add'}
            required={isOther}
            help="Included in the email to the submitter and in the webhook to the integrator."
          >
            <Textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              rows={4}
              placeholder={
                isOther
                  ? 'Describe the problem so the exporter can fix it.'
                  : 'Optional detail to help the exporter correct the request.'
              }
            />
          </Field>
        </DialogBody>
        <DialogFooter note="This is recorded against the request permanently.">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!canSubmit}
            loading={reject.isPending}
            onClick={() => reject.mutate()}
          >
            Reject the request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * REQUEST CLARIFICATION — the middle ground the legacy system lacked (spec §7.1).
 *
 * Without this, a chamber that needs one more invoice has to reject a fixable request,
 * which destroys the link between the problem and its fix and makes rejection reporting
 * meaningless. The checklist comes from the same shared list the registration
 * information-request flow uses (§13.2), so a chamber maintains it once.
 */
export function ClarificationDialog({
  document: doc,
  open,
  onOpenChange,
}: {
  document: DocumentDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const finish = useDecisionMutation(doc.id, () => onOpenChange(false));
  const items = useMasterData(MasterDataListType.CLARIFICATION_ITEM);

  const [selected, setSelected] = useState<string[]>([]);
  const [freeText, setFreeText] = useState('');
  const [respondByDays, setRespondByDays] = useState(14);

  const canSubmit = selected.length > 0 || freeText.trim().length > 0;

  const clarify = useMutation({
    mutationFn: () =>
      api.post(`/api/console/documents/${doc.id}/request-clarification`, {
        itemIds: selected,
        freeText: freeText.trim() || null,
        respondByDays,
      }),
    onSuccess: () =>
      finish(() =>
        toast.toast({
          tone: 'success',
          title: 'Clarification requested',
          description: `${doc.organisationName} has a secure link to respond. The request moves out of your queue until they do.`,
        }),
      ),
    onError: (error) =>
      toast.error(
        'Could not send the request',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  const grouped = (items.data ?? []).reduce<Record<string, MasterDataItem[]>>((acc, item) => {
    const key = item.category ?? 'Other';
    (acc[key] ??= []).push(item);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow={doc.senderReference}
          title="Ask for more before deciding"
          description="The request is not rejected. It waits for the submitter and returns to your queue the moment they respond."
        />
        <DialogBody className="flex flex-col gap-4">
          <fieldset className="m-0 border-0 p-0">
            <legend className="[font-size:var(--pv-text-xs)] font-semibold text-[var(--pv-ink-soft)]">
              What do you need?
            </legend>
            <div className="mt-2.5 flex flex-col gap-3.5">
              {Object.entries(grouped).map(([category, categoryItems]) => (
                <div key={category}>
                  <p className="pv-eyebrow mb-1.5 text-[var(--pv-faint)]">{category}</p>
                  <div className="flex flex-col gap-2">
                    {categoryItems.map((item) => (
                      <Checkbox
                        key={item.id}
                        checked={selected.includes(item.id)}
                        onCheckedChange={(checked) =>
                          setSelected((prev) =>
                            checked === true ? [...prev, item.id] : prev.filter((id) => id !== item.id),
                          )
                        }
                        label={item.label}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </fieldset>

          <Field
            label="Anything specific to this request"
            help="Included in the email and in the webhook. Be concrete — the exporter cannot see your screen."
          >
            <Textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              rows={3}
              placeholder="The packing list shows 700 cartons but the invoice shows 720. Please confirm which is correct."
            />
          </Field>

          <Field label="Respond within" help="After this the request shows as overdue in your queue.">
            <NativeSelect
              value={respondByDays}
              onChange={(e) => setRespondByDays(Number(e.target.value))}
            >
              {[3, 7, 14, 21, 30].map((days) => (
                <option key={days} value={days}>
                  {days} days
                </option>
              ))}
            </NativeSelect>
          </Field>
        </DialogBody>
        <DialogFooter note="The submitter gets a link that works without an account.">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={clarify.isPending}
            onClick={() => clarify.mutate()}
          >
            Send the request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * REVOKE — for a certificate already in the world.
 *
 * The consequence is external and immediate: public verification flips to REVOKED, so
 * anyone checking the certificate — a bank, an importer, a customs officer — sees it. The
 * dialog says that plainly, because an operator needs to know this reaches beyond the
 * system before they press it.
 */
export function RevokeDialog({
  document: doc,
  open,
  onOpenChange,
}: {
  document: DocumentDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const finish = useDecisionMutation(doc.id, () => onOpenChange(false));

  const [reason, setReason] = useState('');
  const [createReissue, setCreateReissue] = useState(true);
  const [confirmation, setConfirmation] = useState('');

  // Typing the certificate number back. Revocation is externally visible and
  // irreversible, and a misclick on a busy queue is a real risk.
  const confirmed = confirmation.trim() === (doc.certificateNo ?? '');
  const canSubmit = reason.trim().length > 0 && confirmed;

  const revoke = useMutation({
    mutationFn: () =>
      api.post<{ reissueDocumentId: string | null }>(`/api/console/documents/${doc.id}/revoke`, {
        reason: reason.trim(),
        createReissue,
      }),
    onSuccess: (result) =>
      finish(() =>
        toast.toast({
          tone: 'success',
          title: `Certificate ${doc.certificateNo} revoked`,
          description: result.reissueDocumentId
            ? 'A replacement has been placed in your pending queue.'
            : 'Anyone verifying this certificate will now see that it has been revoked.',
        }),
      ),
    onError: (error) =>
      toast.error(
        'Could not revoke this certificate',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow={doc.certificateNo ?? doc.senderReference}
          title="Revoke this certificate"
          description="This certificate has already been issued and may be in someone else's hands."
        />
        <DialogBody className="flex flex-col gap-4">
          <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="This is visible outside the system">
            Public verification for {doc.certificateNo} changes to <strong>Revoked</strong>
            immediately. If the exporter has already sent it to an importer or a bank, they will
            need to be told. The number is never reused.
          </Callout>

          <Field
            label="Why is it being revoked?"
            required
            help="Recorded permanently and included in the email to the exporter."
          >
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Issued against the wrong consignee; the exporter has supplied corrected documents."
            />
          </Field>

          <Checkbox
            checked={createReissue}
            onCheckedChange={(v) => setCreateReissue(v === true)}
            label="Put a replacement in the pending queue"
            description="Copies the submitted data into a new request linked to this one. It draws its own certificate number when you approve it."
          />

          <Field
            label={`Type ${doc.certificateNo} to confirm`}
            required
            error={confirmation && !confirmed ? 'That does not match the certificate number.' : null}
          >
            <Input
              mono
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={doc.certificateNo ?? ''}
              autoComplete="off"
            />
          </Field>
        </DialogBody>
        <DialogFooter note="Revocation cannot be undone.">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!canSubmit}
            loading={revoke.isPending}
            onClick={() => revoke.mutate()}
          >
            Revoke
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
